// Lucky-Wheel service (Firestore-backed).
// - settings/wheel      → single config doc
// - wheel_spins/{orderId} → one per order (enforces 1 spin per order)
import { doc, getDoc, setDoc, getDocs, collection, query, where, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase/config";
import { getById, updateDocById } from "./firestore";

const CFG_COL = "settings";
const CFG_ID = "wheel";

export const DEFAULT_WHEEL_CONFIG = {
  enabled: true,
  min_order_azn: 50,
  slices: [
    { number: 1, label: "10% Endirim", type: "coupon_percent", percent: 10, weight: 3 },
    { number: 2, label: "Vinyl Poster", type: "gift_item", weight: 1 },
    { number: 3, label: "5% Endirim", type: "coupon_percent", percent: 5, weight: 5 },
    { number: 4, label: "Pulsuz Çatdırılma", type: "free_shipping", weight: 2 },
    { number: 5, label: "15% Endirim", type: "coupon_percent", percent: 15, weight: 2 },
    { number: 6, label: "Boş", type: "empty", weight: 3 },
    { number: 7, label: "20% Endirim", type: "coupon_percent", percent: 20, weight: 1 },
    { number: 8, label: "Sürpriz Hədiyyə", type: "gift_item", weight: 1 },
  ],
};

// Normalize potentially-object localized value to a printable string.
function toStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v === "number") return String(v);
  if (typeof v === "object") {
    return v.az || v.en || v.ru || Object.values(v).find((x) => typeof x === "string") || "";
  }
  return String(v);
}

export async function getWheelConfig() {
  try {
    const found = await getById(CFG_COL, CFG_ID);
    if (found) {
      const merged = { ...DEFAULT_WHEEL_CONFIG, ...found };
      // Force string labels — protect UI from legacy/bad data shapes
      if (Array.isArray(merged.slices)) {
        merged.slices = merged.slices.map((s, i) => ({
          number: Number(s.number) || (i + 1),
          label: toStr(s.label),
          type: toStr(s.type) || "empty",
          percent: Number(s.percent) || 0,
          weight: Math.max(1, Number(s.weight) || 1),
        }));
      }
      return merged;
    }
  } catch { /* ignore */ }
  return { ...DEFAULT_WHEEL_CONFIG };
}

export async function saveWheelConfig(cfg) {
  const payload = {
    enabled: !!cfg.enabled,
    min_order_azn: Number(cfg.min_order_azn) || 0,
    slices: (cfg.slices || []).map((s, i) => ({
      number: Number(s.number) || (i + 1),
      label: String(s.label || ""),
      type: s.type || "empty",
      percent: Number(s.percent) || 0,
      weight: Math.max(1, Number(s.weight) || 1),
    })),
    updated_at: serverTimestamp(),
  };
  await setDoc(doc(db, CFG_COL, CFG_ID), payload, { merge: true });
  return payload;
}

// Pick a weighted-random slice index.
export function pickWinnerIndex(slices) {
  const total = slices.reduce((s, x) => s + (Number(x.weight) || 1), 0);
  let r = Math.random() * total;
  for (let i = 0; i < slices.length; i++) {
    r -= Number(slices[i].weight) || 1;
    if (r <= 0) return i;
  }
  return slices.length - 1;
}

// Firestore: spins live under wheel_spins/{orderId}.
export async function getSpinForOrder(orderId) {
  if (!orderId) return null;
  const snap = await getDoc(doc(db, "wheel_spins", orderId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

export async function recordSpin(orderId, userId, slice) {
  const payload = {
    order_id: orderId,
    user_id: userId || null,
    slice_number: slice.number,
    slice_label: slice.label,
    slice_type: slice.type,
    percent: Number(slice.percent) || 0,
    status: slice.type === "empty" ? "expired" : "pending",
    created_at: serverTimestamp(),
  };
  await setDoc(doc(db, "wheel_spins", orderId), payload);
  return payload;
}

export async function listUserSpins(userId) {
  if (!userId) return [];
  try {
    const snap = await getDocs(query(collection(db, "wheel_spins"), where("user_id", "==", userId)));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch { return []; }
}
